import type { TypePolicies } from "@apollo/client"

/**
 * Politiques de cache Apollo, sorties du provider pour être testées sur un `InMemoryCache` réel
 * (`__tests__/apolloTypePolicies.test.ts`).
 *
 * ## Objets embarqués lus par PLUSIEURS documents aux sélections différentes
 *
 * Un objet sans identifiant (`keyFields: false`, ou un type sans `id`) est stocké DANS le champ qui
 * le porte. Sans `merge`, chaque réponse REMPLACE l'objet entier : les champs que seule une autre
 * requête sélectionnait disparaissent, cette requête-là n'est plus complète en cache, et ses
 * observateurs `cache-first` / `cache-and-network` repartent au réseau — dont la réponse écrase à
 * son tour la première. Mesuré en préprod le 2026-10-27 (TOTAL, « Mes PCA ») :
 * - `getBusinessSettings` (`TenantTypeOutput`) lu par `GetBusinessSettings`,
 *   `GetBusinessTableSettings` (`tableStyle` seul), `GetBusinessSampleSettings`… : 3 à 4 appels par
 *   page au lieu d'un, `tableStyle` absent du cache ;
 * - `referential.ux.createForm` (`uxFormComponentType`) : une réponse sans `uri` remplaçait celle
 *   de `getReferentielQuery`, qui relançait ses 154 Ko à chaque ouverture de fiche.
 *
 * `merge: true` fusionne les champs entrants dans l'objet existant ; un champ sélectionné revient
 * toujours avec sa valeur serveur (y compris `null`), seul un champ NON sélectionné est conservé.
 */
export const apolloTypePolicies: TypePolicies = {
  // Ces types UX sont des sous-objets "embedded" (pas d'ID), et peuvent être renvoyés
  // partiellement (ex: createForm dans une requête, infoTable dans une autre).
  uxReferentialType: { keyFields: false, merge: true },
  uxTableComponentType: { keyFields: false, merge: true },
  uxFormComponentType: { keyFields: false, merge: true },
  uxMenuItemType: {
    fields: {
      folders: {
        merge(_existing: any, incoming: any) {
          return incoming
        },
      },
    },
  },
  ReferentialType: {
    fields: {
      // Referentiel.ux est un objet "embedded" sans ID : on merge shallow pour éviter la perte
      // de champs lors de réponses partielles (ex: createForm seulement vs infoTable seulement).
      ux: {
        merge(existing = {}, incoming = {}) {
          return { ...existing, ...incoming }
        },
      },
    },
  },
  // Réglages du tenant : un seul objet, sans `id`, lu par une dizaine de documents.
  TenantTypeOutput: { merge: true },
}
